import assert from 'assert';
import {useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import * as store from '@/store';
import sdk from '@/sdk';
import {DocumentOperateType, IPublishSquareData} from '@/interface';
import toast, {ToastType} from '@/components/Toast';
import ConfirmDialog from '@/components/ConfirmDialog';
import Text, {TextColor, TextType} from '@/components/Text';
import Checkbox from '@/components/Checkbox';
import {feLogger, deletePage} from '@/buried';
import {getDocsInMeeting} from '@/utils/document/meetingMode';
import {BOOK_TYPE, DOC_TYPE} from '@/constants/document/doc';

import styles from './index.module.scss';
import {isPublishOnlyToPerson, isPublishToPerson} from '@/utils/document/square';

interface DeleteDocumentConfirmDialogProps {
    deleteDocument?: {docGuid: string, doc: WizDocument};
    publishToSquareData?: IPublishSquareData;
}

const getDeleteToast = (publishToSquareData?: IPublishSquareData, doc?: WizDocument) => {
    if (isPublishToPerson(publishToSquareData)) {
        return isPublishOnlyToPerson(publishToSquareData)
            ? 'confirmDeleteDocPerson'
            : 'confirmDeleteDocPersonAndSquare';
    }
    return doc?.type === 2 ? 'confirmDeleteDirectoryTitle' : 'confirmDeleteDocTitle';
};

function DeleteDocumentConfirmDialog(props: DeleteDocumentConfirmDialogProps) {
    const intl = useIntl();
    const {
        deleteDocument,
        publishToSquareData,
    } = props;
    const [open, setOpen] = useState(false);
    const [withDescendants, setWithDescendants] = useState(false);
    const docGuid = deleteDocument ? deleteDocument.docGuid : '';
    const doc = deleteDocument ? deleteDocument.doc : null;
    const isParent = (doc && doc.childCount) ? doc.childCount > 0 : false;

    function handleCancel() {
        setWithDescendants(false);
        setOpen(false);
    }

    function deleteSuccess() {
        handleCancel();
        doc && store.setDocumentOperate({
            docGuidList: [docGuid],
            docList: [doc],
            type: DocumentOperateType.Delete,
            data: {
                withDescendants,
            },
        });
        toast({
            content: intl.formatMessage({id: 'successDeleteDoc'}),
            type: ToastType.Success,
        });
    }

    async function deleteDoc() {
        // 我做了一个大胆的决定这里的关于操作左侧树的逻辑还有跳转逻辑都放在store.setDocumentOperate里
        // 树因为操作发生的改变应该集中处理  这里就只做接口请求就行了
        try {
            const repository = store.getCurRepository();
            assert(repository);
            assert(doc);
            const ksServer = await sdk.getKsServer(repository);
            await ksServer.deleteDocument(docGuid, withDescendants);
            deleteSuccess();
            return true;
        } catch (err) {
            console.error(err);
            if (err.code === 404) {
                deleteSuccess();
                return true;
            }
            const errContent = doc && doc.bookType === BOOK_TYPE.IS_BOOK
                ? 'errorDeleteBook' : 'errorDeleteDoc';
            toast({
                content: intl.formatMessage({id: errContent}),
                type: ToastType.Error,
            });
            return false;
        }
    }

    async function handleConfirm() {
        // 确认删除文档添加埋点
        feLogger(deletePage);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        return deleteDoc();
    }

    function handleChange(event: React.ChangeEvent<Element>, checked: boolean) {
        setWithDescendants(checked);
    }

    useEffect(() => {
        if (docGuid) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            getDocsInMeeting().then(docs => {
                if (docs && docs.includes(docGuid)) {
                    toast({
                        type: ToastType.Error,
                        content: <span>{intl.formatMessage({id: 'deleteMeetingDocTip'})}</span>,
                    });
                } else {
                    setOpen(true);
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteDocument, docGuid]);

    // 删除 Dialog 的国际化文案 id。
    // 如果发布到知识广场，需要用另一个文案。
    const deleteI18nId = getDeleteToast(publishToSquareData, deleteDocument?.doc);

    return (
        <ConfirmDialog
            visible={open}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            showCloseX={false}
            showHeader={false}
            className={styles.root}
        >
            <div className={styles.title}>
                <Text type={TextType.T14} color={TextColor.Dark}>
                    <FormattedMessage id={deleteI18nId} />
                </Text>
            </div>
            <div className={styles.info}>
                <Text type={TextType.T14} color={TextColor.Secondary}>
                    <FormattedMessage id={'confirmDeleteDocInfo'} />
                </Text>
            </div>
            {isParent && (
                <div className={styles.deleteDocChildrenInfo}>
                    <Checkbox onChange={handleChange}>
                        <FormattedMessage
                            id={deleteDocument?.doc?.type === DOC_TYPE.DIRECTORY ?
                                'confirmDeleteDirectoryChildrenInfo' : 'confirmDeleteDocChildrenInfo'}
                        />
                    </Checkbox>
                </div>
            )}
        </ConfirmDialog>
    );
}

const DeleteDocumentConfirmDialogImpl = store.connect([
    store.KEYS.DELETE_DOCUMENT,
    store.KEYS.PUBLISH_TO_SQUARE_DATA,
    store.KEYS.IS_KU_SHARE_MODE,
])(DeleteDocumentConfirmDialog);

export default DeleteDocumentConfirmDialogImpl;
